import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Users, Briefcase, CheckCircle, Clock, FileText,
  Plus, ChevronRight, TrendingUp, XCircle
} from 'lucide-react';
import styles from '../../reports/dashboard.module.scss';
import { apiBaseUrl } from 'services/config';
import { useTheme } from 'context/ThemeContext';

const API = apiBaseUrl;
const ax  = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` } });

// ─── Fallback data (shown before API loads) ──────────────────────
const EMPTY_TREND = [
  { month:'Jan',count:0 },{ month:'Feb',count:0 },{ month:'Mar',count:0 },
  { month:'Apr',count:0 },{ month:'May',count:0 },{ month:'Jun',count:0 },
];

// ─── Animated counter ────────────────────────────────────────────
function useCounter(target, duration = 1100) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target) return;
    let current = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      current += step;
      if (current >= target) { setVal(target); clearInterval(timer); }
      else setVal(Math.floor(current));
    }, 16);
    return () => clearInterval(timer);
  }, [target]);
  return val;
}

// ─── Stat Card ───────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, suffix, color, trend, trendDir, delay }) {
  const count = useCounter(value);
  return (
    <div className={`${styles.statCard} ${styles[color]}`} style={{ animationDelay: delay }}>
      <div className={`${styles.statIcon} ${styles[color]}`}>
        <Icon size={18} />
      </div>
      {trend && <span className={`${styles.statTrend} ${styles[trendDir]}`}>{trend}</span>}
      <div className={styles.statValue}>{count.toLocaleString()}{suffix || ''}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

// ─── Custom tooltip ──────────────────────────────────────────────
const ChartTip = ({ active, payload, label }) => {
  const { T } = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:8, padding:'10px 14px', fontFamily:'Outfit,sans-serif', fontSize:13 }}>
      <p style={{ color:T.muted, margin:'0 0 6px', fontSize:11 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color:p.color, margin:'3px 0', fontWeight:600 }}>
          {p.name}: <span style={{ color:T.text }}>{p.value}</span>
        </p>
      ))}
    </div>
  );
};

// ─── Badge ───────────────────────────────────────────────────────
function Badge({ status }) {
  const cls = {
    'Pipeline':            'pending',
    'Interview Scheduled': 'inprogress',
    'Selected':            'passed',
    'Rejected':            'failed',
    'On Hold':             'onhold',
    'Offered':             'pending',
    'Accepted':            'passed',
  };
  return <span className={`${styles.badge} ${styles[cls[status] || 'pending']}`}>{status}</span>;
}

// ─── Dashboard ───────────────────────────────────────────────────
export default function RecruitmentDashboard() {
  const { T } = useTheme();
  const navigate = useNavigate();

  // Pipeline status colors — same semantic mapping as ApplicantPipeline.jsx
  const STATUS_COLOR = {
    'Pipeline':            T.cyan,
    'Interview Scheduled': T.purple,
    'Selected':            T.green,
    'Rejected':            T.red,
    'On Hold':             T.amber,
  };
  const BAR_COLORS = [T.cyan, T.purple, T.green, T.amber, T.red, T.cyan];
  const DONUT_FALLBACK = [
    { name:'Pipeline',            value:0, color:STATUS_COLOR['Pipeline'] },
    { name:'Interview Scheduled', value:0, color:STATUS_COLOR['Interview Scheduled'] },
    { name:'Selected',            value:0, color:STATUS_COLOR['Selected'] },
    { name:'Rejected',            value:0, color:STATUS_COLOR['Rejected'] },
    { name:'On Hold',             value:0, color:STATUS_COLOR['On Hold'] },
  ];

  const [data,     setData]    = useState(null);
  const [loading,  setLoading] = useState(true);
  const [greeting, setGreeting]= useState('');
  const [dateStr,  setDateStr] = useState('');

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening');
    setDateStr(new Date().toLocaleDateString('en-IN', { weekday:'long', year:'numeric', month:'long', day:'numeric' }));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/recruitment/dashboard`, ax());
      setData(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // ── Derived values ─────────────────────────────────────────────
  const pl            = data?.pipeline   || {};
  const iv            = data?.interviews || {};
  const of            = data?.offers     || {};
  const totalApplicants = pl.total              || 0;
  const scheduled       = pl.interview_scheduled|| 0;
  const selected        = pl.selected           || 0;
  const offersTotal     = of.offered            || 0;
  const offersAccepted  = of.accepted           || 0;
  const openPositions   = data?.open_positions  || 0;

  // Acceptance rate %
  const acceptRate = offersTotal > 0 ? Math.round((offersAccepted / offersTotal) * 100) : 0;

  // Monthly trend — shape for AreaChart
  const trendData = (data?.monthly_trend || EMPTY_TREND).map(t => ({
    month: t.month || t.day,
    applicants: t.count,
    // interviews from IV data proportionally — best estimate
    interviews: Math.round((t.count || 0) * 0.6),
  }));

  // Source breakdown for BarChart
  const sourceData = (data?.sources || []).map(s => ({
    source: s.source || 'Unknown',
    count:  s.count  || 0,
  }));

  // Donut — pipeline distribution
  const pipelineTotal = totalApplicants || 1;
  const donutData = [
    { name:'Pipeline',            value: pl.in_pipeline         || 0, color:STATUS_COLOR['Pipeline'] },
    { name:'Interview Scheduled', value: pl.interview_scheduled || 0, color:STATUS_COLOR['Interview Scheduled'] },
    { name:'Selected',            value: pl.selected            || 0, color:STATUS_COLOR['Selected'] },
    { name:'Rejected',            value: pl.rejected            || 0, color:STATUS_COLOR['Rejected'] },
    { name:'On Hold',             value: pl.on_hold             || 0, color:STATUS_COLOR['On Hold'] },
  ].filter(d => d.value > 0);

  const donutDisplay = donutData.length > 0 ? donutData : DONUT_FALLBACK;

  // Recent applicants
  const recentApplicants = data?.recent_applicants || [];

  return (
    <div className={styles.dashboardRoot}>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <p className={styles.greeting}>{greeting}</p>
          <h1>Recruitment <span>Dashboard</span></h1>
          <p className={styles.subtitle}>{dateStr}</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnSecondary} onClick={() => navigate('/admin/hrms/recruitment/pipeline')}>
            <Plus size={14} /> Add Candidate
          </button>
          <button className={styles.btnPrimary} onClick={() => navigate('/admin/interviews')}>
            <Users size={14} /> View Pipeline
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className={styles.statsGrid}>
        <StatCard icon={Users}       label="Total Applicants"  value={totalApplicants} color="cyan"   trend={`${openPositions} open roles`} trendDir="up"   delay="0.05s" />
        <StatCard icon={Clock}       label="Interviews Scheduled" value={scheduled}    color="purple" trend="awaiting"                       trendDir="flat" delay="0.10s" />
        <StatCard icon={CheckCircle} label="Selected"          value={selected}        color="green"  trend={`${totalApplicants > 0 ? Math.round((selected/totalApplicants)*100) : 0}% rate`} trendDir="up" delay="0.15s" />
        <StatCard icon={FileText}    label="Offers Sent"       value={offersTotal}     color="amber"  trend="pending response"               trendDir="flat" delay="0.20s" />
        <StatCard icon={TrendingUp}  label="Offer Acceptance"  value={acceptRate} suffix="%" color="cyan" trend={`${offersAccepted} accepted`} trendDir="up" delay="0.25s" />
      </div>

      {/* Charts Row */}
      <div className={styles.chartsRow}>

        {/* Monthly Applicant Trend */}
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Applicant Activity — Last 6 Months</h3>
            <span className={styles.chartPeriod}>Monthly</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trendData} margin={{ top:5, right:5, left:-20, bottom:0 }}>
              <defs>
                <linearGradient id="ga" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={T.cyan} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={T.cyan} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gb" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={T.green} stopOpacity={0.25}/>
                  <stop offset="95%" stopColor={T.green} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.toggle_bg}/>
              <XAxis dataKey="month" tick={{ fill:T.muted, fontSize:11 }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fill:T.muted, fontSize:11 }} axisLine={false} tickLine={false}/>
              <Tooltip content={<ChartTip/>}/>
              <Area type="monotone" dataKey="applicants"  name="Applicants"  stroke={T.cyan} strokeWidth={2} fill="url(#ga)" dot={false}/>
              <Area type="monotone" dataKey="interviews"  name="Interviewed" stroke={T.green} strokeWidth={2} fill="url(#gb)" dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Candidate Sources */}
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Candidates by Source</h3>
            <span className={styles.chartPeriod}>All Time</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={sourceData.length > 0 ? sourceData : [
                {source:'LinkedIn',count:0},{source:'Referral',count:0},
                {source:'Direct',count:0},{source:'Job Board',count:0},
              ]}
              margin={{ top:5, right:5, left:-20, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.toggle_bg}/>
              <XAxis dataKey="source" tick={{ fill:T.muted, fontSize:11 }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fill:T.muted, fontSize:11 }} axisLine={false} tickLine={false}/>
              <Tooltip content={<ChartTip/>}/>
              <Bar dataKey="count" name="Candidates" radius={[4,4,0,0]}>
                {(sourceData.length > 0 ? sourceData : [{},{},{},{}]).map((_,i) => (
                  <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} fillOpacity={0.85}/>
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row */}
      <div className={styles.bottomRow}>

        {/* Pipeline Donut */}
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Pipeline Distribution</h3>
            <span className={styles.chartPeriod}>Current</span>
          </div>
          <div className={styles.donutWrap}>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={donutDisplay}
                  cx="50%" cy="50%"
                  innerRadius={52} outerRadius={78}
                  dataKey="value"
                  paddingAngle={3}>
                  {donutDisplay.map((d, i) => <Cell key={i} fill={d.color}/>)}
                </Pie>
                <Tooltip
                  formatter={(v) => [v, 'Candidates']}
                  contentStyle={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:8, fontFamily:'Outfit' }}/>
              </PieChart>
            </ResponsiveContainer>
            <div className={styles.donutLegend}>
              {donutDisplay.map(d => {
                const pct = totalApplicants > 0 ? Math.round((d.value / totalApplicants) * 100) : 0;
                return (
                  <div key={d.name} className={styles.legendItem}>
                    <div className={styles.legendLeft}>
                      <span className={styles.dot} style={{ background:d.color }}/>
                      {d.name}
                    </div>
                    <div className={styles.scoreBar}>
                      <div className={styles.bar}>
                        <div className={styles.fill} style={{ width:`${pct}%`, background:d.color }}/>
                      </div>
                      <span className={styles.val} style={{ color:d.color }}>{d.value}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent Applicants Table */}
        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <h3>Recent Applicants</h3>
            <button className={styles.viewAll} onClick={() => navigate('/admin/hrms/recruitment/pipeline')}>
              View All <ChevronRight size={12} style={{ verticalAlign:'middle' }}/>
            </button>
          </div>
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Position</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Added</th>
                </tr>
              </thead>
              <tbody>
                {recentApplicants.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign:'center', padding:32, color:T.dim, fontSize:13 }}>
                      No applicants yet — add candidates from the Pipeline page
                    </td>
                  </tr>
                ) : recentApplicants.map((a, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight:600 }}>{a.first_name} {a.last_name}</td>
                    <td className={styles.dimText}>{a.position || '—'}</td>
                    <td className={styles.dimText}>{a.source   || '—'}</td>
                    <td><Badge status={a.status || 'Pipeline'}/></td>
                    <td className={styles.dimText}>
                      {a.created_at
                        ? new Date(a.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short' })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
}
